import {
    AdvancedMarker,
    InfoWindow,
    Map,
    MapCameraChangedEvent,
    useAdvancedMarkerRef,
    useMap,
} from '@vis.gl/react-google-maps';
import { AutoSearchCheckbox } from './AutoSearchCheckbox';
import { CatalogNearMe } from '@/redux/modules/auctionsNearMe/catalogsNearMe.types';
import { InfoWindowContent } from './InfoWindowContent';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';

export type MapBounds = {
    northEast: {
        lat: number;
        lng: number;
    };
    southWest: {
        lat: number;
        lng: number;
    };
};

type Props = {
    bounds: MapBounds;
    catalogs: CatalogNearMe[];
    isGeocoding: boolean;
    onBoundsChange: (newBounds: MapBounds) => void;
    onLoad?: () => void;
    onMarkerClick?: (catalogId: number) => void;
};

export const AuctionsNearMeGoogleMap = ({
    bounds,
    catalogs,
    isGeocoding,
    onBoundsChange,
    onLoad,
    onMarkerClick,
}: Props): ReactElement => {
    const map = useMap();
    const [selectedCatalog, setSelectedCatalog] = useState<CatalogNearMe | null>(null);
    const [selectedMarkerRef, selectedMarker] = useAdvancedMarkerRef();
    const [autoSearch, setAutoSearch] = useState(true);
    const prevBoundsRef = useRef(bounds);
    const isProgrammaticChangeRef = useRef(false);

    // Notify parent when Google Maps is loaded and ready
    useEffect(() => {
        if (map && onLoad) {
            onLoad();
        }
    }, [map, onLoad]);

    // Convert our MapBounds to google.maps.LatLngBoundsLiteral for the Map component
    const defaultBounds = {
        east: bounds.northEast.lng,
        north: bounds.northEast.lat,
        south: bounds.southWest.lat,
        west: bounds.southWest.lng,
    };

    // When bounds prop changes (e.g., from search), update the map view
    useEffect(() => {
        if (!map) {
            return;
        }

        // Only update if bounds actually changed (not just re-render)
        const boundsChanged =
            prevBoundsRef.current.northEast.lat !== bounds.northEast.lat ||
            prevBoundsRef.current.northEast.lng !== bounds.northEast.lng ||
            prevBoundsRef.current.southWest.lat !== bounds.southWest.lat ||
            prevBoundsRef.current.southWest.lng !== bounds.southWest.lng;

        if (!boundsChanged) {
            return;
        }

        prevBoundsRef.current = bounds;

        // Use Google Maps API to fit the map to the new bounds
        const googleBounds = new google.maps.LatLngBounds(
            { lat: bounds.southWest.lat, lng: bounds.southWest.lng },
            { lat: bounds.northEast.lat, lng: bounds.northEast.lng }
        );

        // Mark this as a programmatic change so handleCameraChanged doesn't trigger a search
        isProgrammaticChangeRef.current = true;
        map.fitBounds(googleBounds);
    }, [bounds, map]);

    const handleMarkerClick = useCallback(
        (catalog: CatalogNearMe) => {
            setSelectedCatalog(catalog);
            if (onMarkerClick) {
                onMarkerClick(catalog.id);
            }
        },
        [onMarkerClick]
    );

    const handleCameraChanged = useCallback(
        (event: MapCameraChangedEvent) => {
            // Skip if we're currently geocoding (initial page load with custom search)
            if (isGeocoding) {
                return;
            }

            // Skip if this was a programmatic change (e.g., from fitBounds)
            if (isProgrammaticChangeRef.current) {
                isProgrammaticChangeRef.current = false;
                return;
            }

            // Only trigger search if "Search as I move the map" is enabled
            if (!autoSearch) {
                return;
            }

            const eventBounds = event.detail.bounds;

            const newBounds: MapBounds = {
                northEast: {
                    lat: eventBounds.north,
                    lng: eventBounds.east,
                },
                southWest: {
                    lat: eventBounds.south,
                    lng: eventBounds.west,
                },
            };

            onBoundsChange(newBounds);
        },
        [onBoundsChange, autoSearch, isGeocoding]
    );

    const handleInfoWindowClose = useCallback(() => {
        setSelectedCatalog(null);
    }, []);

    // Group catalogs by location to show count if multiple catalogs at same location
    const catalogsByLocation = catalogs.reduce(
        (groupedCatalogs, catalog) => {
            const key = `${catalog.lat},${catalog.lon}`;
            if (!groupedCatalogs[key]) {
                groupedCatalogs[key] = [];
            }
            groupedCatalogs[key].push(catalog);
            return groupedCatalogs;
        },
        {} as Record<string, CatalogNearMe[]>
    );

    return (
        <div className="flex flex-col gap-xs">
            <div className="h-[500px] w-[500px]">
                <Map
                    defaultBounds={defaultBounds}
                    fullscreenControl={false}
                    gestureHandling="greedy"
                    mapId="auctions-near-me-map"
                    mapTypeControl={false}
                    onCameraChanged={handleCameraChanged}
                >
                    {Object.entries(catalogsByLocation).map(([locationKey, catalogsAtLocation]) => {
                        const catalog = catalogsAtLocation[0];
                        const isSelected = selectedCatalog?.id === catalog.id;

                        return (
                            <AdvancedMarker
                                key={locationKey}
                                onClick={() => handleMarkerClick(catalog)}
                                position={{ lat: catalog.lat, lng: catalog.lon }}
                                ref={isSelected ? selectedMarkerRef : undefined}
                            />
                        );
                    })}

                    {selectedCatalog && selectedMarker && (
                        <InfoWindow
                            anchor={selectedMarker}
                            onClose={handleInfoWindowClose}
                        >
                            <InfoWindowContent
                                catalog={selectedCatalog}
                                catalogsByLocation={catalogsByLocation}
                            />
                        </InfoWindow>
                    )}
                </Map>
            </div>
            <AutoSearchCheckbox
                checked={autoSearch}
                onChange={(checked) => setAutoSearch(checked)}
            />
        </div>
    );
};
